import { useQuery } from "@tanstack/react-query";
import { getAgent } from "../services/agentService";
import { AGENT_QUERY_KEY } from "./useAgentMissions";

export type AgentIdentity = {
  /** Nom affichable. Jamais vide : repli sur « Main » + 4 caractères de l'id. */
  name: string;
  /** Note qualité sur 5, ou `null` si le backend n'en a pas encore calculé. */
  rating: number | null;
  /** Emoji d'avatar — repli quand aucune photo n'est publiée. */
  avatar: string;
  /** Photo de profil, si le back office en a posé une dans `metadata`. */
  avatarUrl: string | null;
  /** Numéro joignable, si publié. Sans lui, les boutons de contact restent inertes. */
  phone: string | null;
};

/**
 * Identité affichable de la Main assignée à une course, vue CLIENT.
 *
 * `Trip.agent_id` ne donne qu'un UUID. Le schéma `Agent` porte `qualityScore`,
 * mais ni nom, ni photo : ils vivent dans `metadata`, alimentée par le back
 * office. C'est exactement la lecture que fait déjà `candidateToAgent` dans
 * matchingService — même repli, pour que la Main s'appelle pareil pendant la
 * recherche et pendant la course.
 *
 * On ne fabrique pas de note : sans `qualityScore`, `rating` vaut `null` et
 * l'appelant masque l'étoile plutôt que d'afficher un 0 qui se lirait comme
 * « très mauvaise Main ».
 */
export function useAgentIdentity(agentId: string | undefined | null) {
  return useQuery<AgentIdentity>({
    queryKey: [...AGENT_QUERY_KEY, "identity", agentId],
    enabled: Boolean(agentId),
    // Le nom d'une Main ne change pas pendant une course : inutile de le
    // refetcher au retour d'onglet, contrairement à sa position GPS.
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const profile = await getAgent(agentId!);
      const meta = (profile?.metadata ?? {}) as Record<string, unknown>;

      // `avatar` sert d'emoji de repli ; une URL posée dans le même champ par le
      // back office doit être traitée comme une PHOTO, pas rendue en texte.
      const avatarRaw = typeof meta.avatar === "string" ? meta.avatar : "";
      const isUrl = /^https?:\/\//.test(avatarRaw);
      const avatarUrl = typeof meta.avatarUrl === "string" ? meta.avatarUrl : null;

      const phone = [meta.phone, meta.phoneNumber, meta.contact_phone].find(
        (v): v is string => typeof v === "string" && v.trim() !== "",
      );

      return {
        name:
          typeof meta.name === "string" && meta.name.trim() !== ""
            ? meta.name
            : `Main ${(agentId ?? "").slice(0, 4).toUpperCase()}`,
        rating: typeof profile?.qualityScore === "number" ? profile.qualityScore : null,
        avatar: !isUrl && avatarRaw !== "" ? avatarRaw : "🧑",
        avatarUrl: avatarUrl ?? (isUrl ? avatarRaw : null),
        phone: phone ?? null,
      };
    },
  });
}
